import React, { useEffect, useState } from "react";
import {
  Line,
  Bar
} from "react-chartjs-2";

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Tooltip,
  Legend
} from "chart.js";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Tooltip,
  Legend
);

const LogAnalyticsDashboard = () => {
  const [overview, setOverview] = useState(null);
  const [suspicious, setSuspicious] = useState([]);

  useEffect(() => {
    async function fetchData() {
      const o = await fetch("/api/log-analytics/overview").then((r) => r.json());
      const s = await fetch("/api/log-analytics/ip-anomalies").then((r) => r.json());
      setOverview(o);
      setSuspicious(s.suspicious || []);
    }
    fetchData();
  }, []);

  if (!overview) return <div className="p-4 text-white">Loading...</div>;

  // ---------------------------
  // Chart Data Builders
  // ---------------------------

  const dailyChart = {
    labels: overview.timeseriesByDay.map((d) => d.day),
    datasets: [
      {
        label: "Requests",
        data: overview.timeseriesByDay.map((d) => d.count),
        borderColor: "#38bdf8",
        backgroundColor: "rgba(56,189,248,0.3)",
      },
    ],
  };

  const hourlyChart = {
    labels: overview.timeseriesByHour.map((d) => d.hour),
    datasets: [
      {
        label: "Requests",
        data: overview.timeseriesByHour.map((d) => d.count),
        borderColor: "#a78bfa",
        backgroundColor: "rgba(167,139,250,0.3)",
      },
    ],
  };

  const statusChart = {
    labels: Object.keys(overview.byStatus),
    datasets: [
      {
        label: "Status Codes",
        data: Object.values(overview.byStatus),
        backgroundColor: "#34d399",
      },
    ],
  };

  const methodChart = {
    labels: Object.keys(overview.byMethod),
    datasets: [
      {
        label: "HTTP Methods",
        data: Object.values(overview.byMethod),
        backgroundColor: "#fb7185",
      },
    ],
  };

  return (
    <div className="p-4 min-h-screen bg-gray-900 text-gray-300 [&_li]:text-white">
      <h1 className="text-2xl font-bold mb-2">📡 Log Analytics (Access.log)</h1>
      <p className="text-sm mb-6 text-gray-400">
        Timeframe: {overview.timeframe.firstTime} → {overview.timeframe.lastTime}
      </p>

      {/* ---------------- KPIs ---------------- */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <Kpi title="Total Requests" value={overview.totalRequests} />
        <Kpi title="Unique IPs" value={overview.uniqueIPs} />
        <Kpi
          title="Total Bandwidth (MB)"
          value={(overview.totalBytes / (1024 * 1024)).toFixed(2)}
        />
        <Kpi title="Suspicious IPs" value={suspicious.length} />
      </div>

      {/* ---------------- Charts ---------------- */}
      <div className="grid md:grid-cols-2 gap-6 mb-6">
        <ChartCard title="Requests Per Day">
          <Line data={dailyChart} />
        </ChartCard>

        <ChartCard title="Requests Per Hour">
          <Line data={hourlyChart} />
        </ChartCard>
      </div>

      <div className="grid md:grid-cols-2 gap-6 mb-6">
        <ChartCard title="Status Codes">
          <Bar data={statusChart} />
        </ChartCard>

        <ChartCard title="HTTP Methods">
          <Bar data={methodChart} />
        </ChartCard>
      </div>

      {/* ---------------- Tables ---------------- */}
      <div className="grid md:grid-cols-2 gap-6 mb-6">
        <TableCard
          title="Top Endpoints"
          rows={overview.topEndpoints}
          columns={["path", "count"]}
        />
        <TableCard
          title="Top IPs"
          rows={overview.topIPs}
          columns={["ip", "count"]}
        />
      </div>

      <div className="grid">
        <SuspiciousIPs suspicious={suspicious} />
      </div>
    </div>
  );
};

export default LogAnalyticsDashboard;

// --------------------------------------------------------------

const Kpi = ({ title, value }) => (
  <div className="bg-gray-800 p-4 rounded-xl">
    <div className="text-xs text-gray-400">{title}</div>
    <div className="text-xl font-bold">{value}</div>
  </div>
);

const ChartCard = ({ title, children }) => (
  <div className="bg-gray-800 p-4 rounded-xl">
    <h2 className="text-lg mb-2 font-semibold">{title}
    { title==="Status Codes" && (
      <span className="text-yellow-200 bg-red-500 cursor-pointer group relative">
          <span className="bg-red-500">?</span>
          <div className="absolute left-4 top-0 hidden group-hover:block bg-gray-900 text-xs text-gray-300 p-2 rounded-lg w-52 shadow-lg border border-gray-700 z-20">
            <div><span className="text-green-400 font-bold">200</span> – Success</div>
            <div><span className="text-yellow-400 font-bold">400–499</span> – Client Errors</div>
            <div><span className="text-red-400 font-bold">500–599</span> – Server Errors</div>
          </div>
          <div className="absolute left-4 top-0 hidden group-hover:block bg-gray-900 text-xs text-gray-300 p-2 rounded-lg w-52 shadow-lg border border-gray-700 z-20">
            <div className="font-semibold text-gray-400 mb-1">Status Codes</div>

            {/* Success */}
            <div><span className="text-green-400 font-bold">200</span> – OK</div>
            <div><span className="text-green-400 font-bold">201</span> – Created</div>
            <div><span className="text-green-400 font-bold">202</span> – Accepted</div>
            <div><span className="text-green-400 font-bold">206</span> – Partial Content</div>
            <div><span className="text-blue-400 font-bold">304</span> – Cached / Not Modified</div>

            <div className="mt-2 font-semibold text-gray-400 mb-1">Client Errors</div>

            <div><span className="text-yellow-400 font-bold">401</span> – Unauthorized</div>
            <div><span className="text-yellow-400 font-bold">402</span> – Payment Required</div>
            <div><span className="text-yellow-400 font-bold">403</span> – Forbidden</div>
            <div><span className="text-yellow-400 font-bold">404</span> – Not Found</div>
            <div><span className="text-yellow-400 font-bold">409</span> – Conflict</div>
            <div><span className="text-yellow-400 font-bold">429</span> – Too Many Requests</div>

            <div className="mt-2 font-semibold text-gray-400 mb-1">Server Errors</div>

            <div><span className="text-red-400 font-bold">500</span> – Server Error</div>
          </div>

        </span>
    )}
    </h2>      
    {children}
  </div>
);

{/* <ChartCard title={
    <div className="flex items-center gap-2">
      Status Codes
      <span className="text-yellow-200 cursor-pointer group relative">
        ?
        <div className="absolute left-4 top-0 hidden group-hover:block bg-gray-900 text-xs text-gray-300 p-2 rounded-lg w-52 shadow-lg border border-gray-700 z-20">
          <div><span className="text-green-400 font-bold">200</span> – Success</div>
          <div><span className="text-yellow-400 font-bold">400–499</span> – Client Errors</div>
          <div><span className="text-red-400 font-bold">500–599</span> – Server Errors</div>
        </div>
      </span>
    </div>
  }>
    <Bar data={statusChart} />
</ChartCard> */}


const TableCard = ({ title, rows, columns }) => (
  <div className="bg-gray-800 p-4 rounded-xl overflow-auto">
    <h2 className="text-lg mb-2 font-semibold">{title}</h2>
    <table className="w-full text-sm">
      <thead>
        <tr>
          {columns.map((c) => (
            <th key={c} className="text-left p-2 border-b border-gray-700">
              {c}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={i} className="border-b border-gray-800">
            {columns.map((c) => (
              <td key={c} className="p-2">
                {r[c]}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const SuspiciousIPs = ({ suspicious }) => (
  <div className="bg-gray-800 p-4 rounded-xl">
    <h2 className="text-lg mb-3 font-semibold">⚠️ Suspicious IP Activity</h2>

    <div className="overflow-auto">
      <table className="w-full text-sm">
        <thead>
          <tr>
            <th className="text-left p-2 border-b border-gray-700">IP</th>
            <th className="text-left p-2 border-b border-gray-700">Requests</th>
            <th className="text-left p-2 border-b border-gray-700">Error Rate</th>
            <th className="text-left p-2 border-b border-gray-700">Reasons</th>
          </tr>
        </thead>

        <tbody>
          {suspicious.length === 0 && (
            <tr>
              <td colSpan="4" className="p-2 text-center text-gray-400">
                No suspicious IPs
              </td>
            </tr>
          )}

          {suspicious.map((ip) => (
            <tr key={ip.ip}>
              <td className="p-2 border-b border-gray-900">{ip.ip}</td>
              <td className="p-2 border-b border-gray-900">{ip.total}</td>
              <td className="p-2 border-b border-gray-900">{ip.errorRate}</td>
              <td className="p-2 border-b border-gray-900">
                <ul className="list-disc ml-4">
                  {ip.reasons.map((r, i) => (
                    <li key={i}>{r}</li>
                  ))}
                </ul>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);
